/**
 * RBAC Permission Catalog (Frontend mirror of backend)
 * Defines all available permissions in the system
 */

export const PERMISSION_CATEGORIES = {
  ADMIN: 'admin',
  TRADES: 'trades',
  SIGNALS: 'signals',
  STRATEGIES: 'strategies',
  USERS: 'users',
  ROLES: 'roles',
  MARKETS: 'markets',
  SYSTEM: 'system',
  PORTFOLIOS: 'portfolios',
  ALERTS: 'alerts',
  WATCHLIST: 'watchlist',
  EVENTS: 'events',
  CYCLES: 'cycles',
  DISCLAIMER: 'disclaimer',
  QUOTAS: 'quotas',
  ANNOTATIONS: 'annotations',
  EVALUATIONS: 'evaluations',
  SETTINGS: 'settings',
  WEBHOOKS: 'webhooks',
};

/**
 * Permission definitions with metadata
 * Format: 'category:action'
 */
export const PERMISSIONS = {
  // Admin permissions
  'admin:*': {
    key: 'admin:*',
    category: PERMISSION_CATEGORIES.ADMIN,
    description: 'Full admin access (wildcard)',
  },
  'admin:read': {
    key: 'admin:read',
    category: PERMISSION_CATEGORIES.ADMIN,
    description: 'View admin dashboard and settings',
  },
  'admin:write': {
    key: 'admin:write',
    category: PERMISSION_CATEGORIES.ADMIN,
    description: 'Modify admin settings',
  },

  // Trades permissions
  'trades:*': {
    key: 'trades:*',
    category: PERMISSION_CATEGORIES.TRADES,
    description: 'Full trade access (wildcard)',
  },
  'trades:read': {
    key: 'trades:read',
    category: PERMISSION_CATEGORIES.TRADES,
    description: 'View trades',
  },
  'trades:write': {
    key: 'trades:write',
    category: PERMISSION_CATEGORIES.TRADES,
    description: 'Create and modify trades',
  },
  'trades:delete': {
    key: 'trades:delete',
    category: PERMISSION_CATEGORIES.TRADES,
    description: 'Delete trades',
  },

  // Signals permissions
  'signals:*': {
    key: 'signals:*',
    category: PERMISSION_CATEGORIES.SIGNALS,
    description: 'Full signals access (wildcard)',
  },
  'signals:read': {
    key: 'signals:read',
    category: PERMISSION_CATEGORIES.SIGNALS,
    description: 'View signals',
  },
  'signals:write': {
    key: 'signals:write',
    category: PERMISSION_CATEGORIES.SIGNALS,
    description: 'Create and modify signals',
  },

  // Strategies permissions
  'strategies:*': {
    key: 'strategies:*',
    category: PERMISSION_CATEGORIES.STRATEGIES,
    description: 'Full strategies access (wildcard)',
  },
  'strategies:read': {
    key: 'strategies:read',
    category: PERMISSION_CATEGORIES.STRATEGIES,
    description: 'View strategies',
  },
  'strategies:write': {
    key: 'strategies:write',
    category: PERMISSION_CATEGORIES.STRATEGIES,
    description: 'Create and modify strategies',
  },

  // Users permissions
  'users:*': {
    key: 'users:*',
    category: PERMISSION_CATEGORIES.USERS,
    description: 'Full user management access (wildcard)',
  },
  'users:read': {
    key: 'users:read',
    category: PERMISSION_CATEGORIES.USERS,
    description: 'View user information',
  },
  'users:write': {
    key: 'users:write',
    category: PERMISSION_CATEGORIES.USERS,
    description: 'Modify user information',
  },

  // Roles permissions
  'roles:*': {
    key: 'roles:*',
    category: PERMISSION_CATEGORIES.ROLES,
    description: 'Full role management access (wildcard)',
  },
  'roles:read': {
    key: 'roles:read',
    category: PERMISSION_CATEGORIES.ROLES,
    description: 'View roles',
  },
  'roles:write': {
    key: 'roles:write',
    category: PERMISSION_CATEGORIES.ROLES,
    description: 'Create and modify roles',
  },
  'roles:assign': {
    key: 'roles:assign',
    category: PERMISSION_CATEGORIES.ROLES,
    description: 'Assign and unassign roles to users',
  },

  // Markets permissions
  'markets:*': {
    key: 'markets:*',
    category: PERMISSION_CATEGORIES.MARKETS,
    description: 'Full market data access (wildcard)',
  },
  'markets:read': {
    key: 'markets:read',
    category: PERMISSION_CATEGORIES.MARKETS,
    description: 'View market data',
  },
  'markets:candles': {
    key: 'markets:candles',
    category: PERMISSION_CATEGORIES.MARKETS,
    description: 'Access raw OHLCV candle data',
  },
  'markets:write': {
    key: 'markets:write',
    category: PERMISSION_CATEGORIES.MARKETS,
    description: 'Modify market data and symbols',
  },

  // System permissions
  'system:*': {
    key: 'system:*',
    category: PERMISSION_CATEGORIES.SYSTEM,
    description: 'Full system access (wildcard)',
  },
  'system:settings:read': {
    key: 'system:settings:read',
    category: PERMISSION_CATEGORIES.SYSTEM,
    description: 'View system settings',
  },
  'system:settings:write': {
    key: 'system:settings:write',
    category: PERMISSION_CATEGORIES.SYSTEM,
    description: 'Modify system settings',
  },
  'system:logs:read': {
    key: 'system:logs:read',
    category: PERMISSION_CATEGORIES.SYSTEM,
    description: 'View system logs',
  },

  // Portfolio permissions
  'portfolios:*': {
    key: 'portfolios:*',
    category: PERMISSION_CATEGORIES.PORTFOLIOS,
    description: 'Full portfolio access (wildcard)',
  },
  'portfolios:read': {
    key: 'portfolios:read',
    category: PERMISSION_CATEGORIES.PORTFOLIOS,
    description: 'View portfolios',
  },
  'portfolios:write': {
    key: 'portfolios:write',
    category: PERMISSION_CATEGORIES.PORTFOLIOS,
    description: 'Create and modify portfolios',
  },

  // Alert permissions
  'alerts:*': {
    key: 'alerts:*',
    category: PERMISSION_CATEGORIES.ALERTS,
    description: 'Full alerts access (wildcard)',
  },
  'alerts:read': {
    key: 'alerts:read',
    category: PERMISSION_CATEGORIES.ALERTS,
    description: 'View alerts',
  },
  'alerts:write': {
    key: 'alerts:write',
    category: PERMISSION_CATEGORIES.ALERTS,
    description: 'Create and modify alerts',
  },

  // Watchlist permissions
  'watchlist:*': {
    key: 'watchlist:*',
    category: PERMISSION_CATEGORIES.WATCHLIST,
    description: 'Full watchlist access (wildcard)',
  },
  'watchlist:read': {
    key: 'watchlist:read',
    category: PERMISSION_CATEGORIES.WATCHLIST,
    description: 'View watchlist',
  },
  'watchlist:write': {
    key: 'watchlist:write',
    category: PERMISSION_CATEGORIES.WATCHLIST,
    description: 'Modify watchlist',
  },

  // Events permissions
  'events:*': {
    key: 'events:*',
    category: PERMISSION_CATEGORIES.EVENTS,
    description: 'Full events access (wildcard)',
  },
  'events:read': {
    key: 'events:read',
    category: PERMISSION_CATEGORIES.EVENTS,
    description: 'View events',
  },
  'events:write': {
    key: 'events:write',
    category: PERMISSION_CATEGORIES.EVENTS,
    description: 'Create and modify events',
  },

  // Cycle permissions
  //
  // 🛑 The trailing `:run` is load-bearing: the backend tests the LAST `:` segment
  // against its write-action list, and `rebuild` is not in it — shortening this key
  // silently un-grants it on every API key. Pinned in permissions.test.js.
  //
  // Reaches the API-key dialog and the role editor from this one entry, both via
  // getPermissionsByCategory(). Nothing fetches this file, so a missing entry is
  // invisible in the product — no error, no empty state, just an absent checkbox.
  //
  // No `cycles:*` sibling, unlike every other category here: the BACKEND defines
  // none, and a wildcard the backend does not know would be offered by the role
  // editor and then rejected by isValidPermission on save.
  'cycles:rebuild:run': {
    key: 'cycles:rebuild:run',
    category: PERMISSION_CATEGORIES.CYCLES,
    description: 'Rebuild stored cycle events for a scope (destructive; an API key must dry-run first)',
  },

  // Disclaimer permissions
  'disclaimer:*': {
    key: 'disclaimer:*',
    category: PERMISSION_CATEGORIES.DISCLAIMER,
    description: 'Full disclaimer management access (wildcard)',
  },
  'disclaimer:read': {
    key: 'disclaimer:read',
    category: PERMISSION_CATEGORIES.DISCLAIMER,
    description: 'View disclaimer versions and acceptances',
  },
  'disclaimer:write': {
    key: 'disclaimer:write',
    category: PERMISSION_CATEGORIES.DISCLAIMER,
    description: 'Create and modify disclaimer versions',
  },

  // Quota permissions
  'quotas:*': {
    key: 'quotas:*',
    category: PERMISSION_CATEGORIES.QUOTAS,
    description: 'Full quota management access (wildcard)',
  },
  'quotas:read': {
    key: 'quotas:read',
    category: PERMISSION_CATEGORIES.QUOTAS,
    description: 'View quota settings and user quotas',
  },
  'quotas:write': {
    key: 'quotas:write',
    category: PERMISSION_CATEGORIES.QUOTAS,
    description: 'Modify quota definitions and user overrides',
  },

  // Annotations permissions
  'annotations:*': {
    key: 'annotations:*',
    category: PERMISSION_CATEGORIES.ANNOTATIONS,
    description: 'Full annotation access (wildcard)',
  },
  'annotations:read': {
    key: 'annotations:read',
    category: PERMISSION_CATEGORIES.ANNOTATIONS,
    description: 'View chart annotations',
  },
  'annotations:write': {
    key: 'annotations:write',
    category: PERMISSION_CATEGORIES.ANNOTATIONS,
    description: 'Create, update, and delete chart annotations (R/R tool, hlines, trendlines, etc.)',
  },

  // Evaluations permissions
  'evaluations:*': {
    key: 'evaluations:*',
    category: PERMISSION_CATEGORIES.EVALUATIONS,
    description: 'Full evaluation access (wildcard)',
  },
  'evaluations:read': {
    key: 'evaluations:read',
    category: PERMISSION_CATEGORIES.EVALUATIONS,
    description: 'View backtest results',
  },
  'evaluations:write': {
    key: 'evaluations:write',
    category: PERMISSION_CATEGORIES.EVALUATIONS,
    description: 'Run backtests',
  },

  // Settings permissions
  'settings:*': {
    key: 'settings:*',
    category: PERMISSION_CATEGORIES.SETTINGS,
    description: 'Full settings access (wildcard)',
  },
  'settings:read': {
    key: 'settings:read',
    category: PERMISSION_CATEGORIES.SETTINGS,
    description: 'View account settings',
  },
  'settings:write': {
    key: 'settings:write',
    category: PERMISSION_CATEGORIES.SETTINGS,
    description: 'Update account settings',
  },

  // Webhooks permissions
  'webhooks:*': {
    key: 'webhooks:*',
    category: PERMISSION_CATEGORIES.WEBHOOKS,
    description: 'Full webhook access (wildcard)',
  },
  'webhooks:read': {
    key: 'webhooks:read',
    category: PERMISSION_CATEGORIES.WEBHOOKS,
    description: 'View webhook configurations',
  },
  'webhooks:write': {
    key: 'webhooks:write',
    category: PERMISSION_CATEGORIES.WEBHOOKS,
    description: 'Create, update, and delete webhooks',
  },
};

/**
 * Check if a permission matches another (supports wildcards)
 * Examples:
 *   permissionMatches('*', 'anything:here') => true (superuser)
 *   permissionMatches('admin:*', 'admin:read') => true
 *   permissionMatches('admin:read', 'admin:read') => true
 *   permissionMatches('admin:read', 'admin:write') => false
 *
 * @param {string} userPermission - Permission the user has
 * @param {string} requiredPermission - Permission required for the action
 * @returns {boolean}
 */
export const permissionMatches = (userPermission, requiredPermission) => {
  if (!userPermission || !requiredPermission) {
    return false;
  }

  // Global wildcard - matches everything (superuser)
  if (userPermission === '*') {
    return true;
  }

  // Exact match
  if (userPermission === requiredPermission) {
    return true;
  }

  // Wildcard match (e.g., 'admin:*' matches 'admin:read')
  if (userPermission.endsWith(':*')) {
    const prefix = userPermission.slice(0, -1); // Remove the '*'
    return requiredPermission.startsWith(prefix);
  }

  return false;
};

/**
 * Check if a user has a specific permission
 *
 * @param {string[]} userPermissions - Array of permissions the user has
 * @param {string} requiredPermission - Permission required for the action
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!Array.isArray(userPermissions) || !requiredPermission) {
    return false;
  }

  return userPermissions.some((perm) => permissionMatches(perm, requiredPermission));
};

/**
 * Check if user has any of the specified permissions
 *
 * @param {string[]} userPermissions - Array of permissions the user has
 * @param {string[]} requiredPermissions - Array of permissions, user needs at least one
 * @returns {boolean}
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!Array.isArray(userPermissions) || !Array.isArray(requiredPermissions)) {
    return false;
  }

  return requiredPermissions.some((required) => hasPermission(userPermissions, required));
};

/**
 * Check if user has all of the specified permissions
 *
 * @param {string[]} userPermissions - Array of permissions the user has
 * @param {string[]} requiredPermissions - Array of permissions, user needs all
 * @returns {boolean}
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  if (!Array.isArray(userPermissions) || !Array.isArray(requiredPermissions)) {
    return false;
  }

  return requiredPermissions.every((required) => hasPermission(userPermissions, required));
};

/**
 * Permissions that the backend refuses to grant on external API keys.
 *
 * The backend's apiKeyHandler maintains its own copy of this list and
 * 400s any create-key request that includes one of these scopes (see
 * BLOCKED_API_KEY_SCOPES in serverless-portfolio-tracker
 * src/handlers/apiKeyHandler.js). The reason is data licensing — raw
 * OHLCV data is sourced from Twelve Data's Venture plan, which permits
 * display to end users but prohibits redistribution via external API.
 *
 * `markets:*` is blocked because as a wildcard it implicitly grants
 * `markets:candles` AND `markets:write`, so it would let users bypass
 * the restriction by selecting the broader permission.
 *
 * ⚠️ `markets:candles` is deliberately NOT in this list. The backend
 * permits it for the platform OWNER's own account(s) — the owner
 * consuming their own licensed data through their own agent is
 * single-user internal use, not redistribution (see rawCandleGate.js
 * and its RAW_CANDLE_ACCOUNT_IDS allowlist).
 *
 * The picker gates on `hasPermission(userPermissions, key)`, and
 * markets:candles is not in the default user role, so ordinary users do
 * not see it. Note this is NOT airtight as a UX matter: `permissionMatches`
 * expands wildcards, so an account holding `*` or `markets:*` will see the
 * checkbox even though it is not the platform owner, and will get a 400
 * from the backend on create. That is a papercut, not a hole — ownership
 * is checked server-side by accountId, both at key creation and again at
 * request time on /v2/market-data/:symbol. The boundary is enforced by
 * the server, never by hiding a checkbox. If the papercut becomes real
 * (more than one admin account), drive the picker off the caller-aware
 * `blockedScopes` already returned by GET /v2/api-keys/scopes instead of
 * this mirror.
 *
 * Other consumers of the permission catalogue (e.g. admin role
 * management) intentionally show the full list because admins can still
 * grant these permissions via roles — the restriction only applies to
 * *external API keys*.
 *
 * If the backend list ever changes, update this constant to match.
 */
export const BLOCKED_API_KEY_SCOPES = new Set([
  'markets:*',
]);

/**
 * Get all permissions grouped by category
 *
 * @returns {Object} Permissions grouped by category
 */
export const getPermissionsByCategory = () => {
  const grouped = {};

  Object.values(PERMISSIONS).forEach((permission) => {
    if (!grouped[permission.category]) {
      grouped[permission.category] = [];
    }
    grouped[permission.category].push(permission);
  });

  return grouped;
};

/**
 * Get all permission keys as an array
 *
 * @returns {string[]} Array of permission keys
 */
export const getAllPermissionKeys = () => {
  return Object.keys(PERMISSIONS);
};

/**
 * Validate that a permission key exists
 *
 * @param {string} permissionKey - Permission key to validate
 * @returns {boolean}
 */
export const isValidPermission = (permissionKey) => {
  return permissionKey in PERMISSIONS;
};
